import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Tour } from '@/config/objects/tours';

export interface TopPickTour extends Tour {
  topPickOrder?: number | null;
}

interface TopPicksResponse {
  success: boolean;
  data: TopPickTour[];
  meta?: { max: number };
}

const buildQueryKey = (hotelId: string) => ['HotelTopPicks', hotelId] as const;

/**
 * Fetch a hotel's curated Top Picks tours, in pinned order.
 *
 * @param hotelId - Hotel id (skipped while falsy).
 */
export function useHotelTopPicks(hotelId: string | null | undefined) {
  const apiClient = useApiClient();

  return useQuery<TopPicksResponse>({
    queryKey: buildQueryKey(hotelId ?? ''),
    enabled: Boolean(hotelId),
    queryFn: () =>
      apiClient.get<TopPicksResponse>(`/hotels/${hotelId}/top-picks`),
  });
}

/**
 * Replace a hotel's Top Picks list with the given ordered tour ids.
 * Optimistically updates the cached list (when caller passes the
 * `optimisticTours`) so the UI reflects the new order immediately.
 */
export function useUpdateHotelTopPicks(hotelId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = buildQueryKey(hotelId);

  return useMutation<
    void,
    Error,
    { tourIds: string[]; optimisticTours?: TopPickTour[] }
  >({
    mutationFn: async ({ tourIds }) => {
      await apiClient.put<{ success: boolean }>(
        `/hotels/${hotelId}/top-picks`,
        { tourIds },
      );
    },
    onMutate: async ({ optimisticTours }) => {
      if (!optimisticTours) return;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TopPicksResponse>(queryKey);
      queryClient.setQueryData<TopPicksResponse>(queryKey, (old) => ({
        success: true,
        data: optimisticTours,
        meta: old?.meta,
      }));
      return { previous } as unknown as void;
    },
    onError: (error, _vars, context) => {
      const ctx = context as unknown as { previous?: TopPicksResponse };
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
      toast.error(error.message || 'Failed to update Top Picks');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
